import API from '@/utils/API';
import { cloudfrontURL } from '@/utils/url-builder';
import productSelectionParser from '@/utils/product-selection-parser';
import { ServiceUnavailableError } from '@/utils/API/core/exceptions';
import humps from 'humps';
import retry from 'async/retry';

/** Steps */
export const WELCOME_STEP = 'WELCOME_STEP';
export const PRODUCT_CHOICE_STEP = 'PRODUCT_CHOICE_STEP';
export const NEW_MORTGAGE_CONDITIONS_STEP = 'NEW_MORTGAGE_CONDITIONS_STEP';
export const MORE_INFO_STEP = 'MORE_INFO_STEP';
export const LINK_SEND_STEP = 'LINK_SEND_STEP';
export const ERROR_STEP = 'ERROR_STEP';
export const CUSTOMER_FORM_STEP_1 = 'CUSTOMER_FORM_STEP_1';
export const CUSTOMER_FORM_STEP_2 = 'CUSTOMER_FORM_STEP_2';
export const CUSTOMER_FORM_STEP_3 = 'CUSTOMER_FORM_STEP_3';
export const CUSTOMER_FORM_STEP_4 = 'CUSTOMER_FORM_STEP_4';
export const PROPERTY_FORM_STEP = 'PROPERTY_FORM_STEP';
export const INCOMES_CATEGORY_STEP = 'INCOMES_CATEGORY_STEP';
// export const INCOMES_FORM_STEP = 'INCOMES_FORM_STEP';
export const MORTGAGE_FORM_STEP_1 = 'MORTGAGE_FORM_STEP_1';
export const MORTGAGE_FORM_STEP_2 = 'MORTGAGE_FORM_STEP_2';
export const MORTGAGE_FORM_STEP_3 = 'MORTGAGE_FORM_STEP_3';
export const MORTGAGE_FORM_STEP_4 = 'MORTGAGE_FORM_STEP_4';
export const MORTGAGE_FORM_STEP_5 = 'MORTGAGE_FORM_STEP_5';
export const SUCCESS_STEP = 'SUCCESS_STEP';
export const NO_INCOMES_SUPPORT_STEP = 'NO_INCOMES_SUPPORT_STEP';

/** Choices */
export const NEW_MORTGAGE_CHOICE = 'NEW_MORTGAGE_CHOICE';
export const BUY_MORTGAGE_CHOICE = 'BUY_MORTGAGE_CHOICE';
export const HOME_EQUITY_LOAN_CHOICE = 'HOME_EQUITY_LOAN_CHOICE';
export const CONFIRM_CHOICE = 'CONFIRM_CHOICE';
export const DECLINE_CHOICE = 'DECLINE_CHOICE';
export const NOT_SURE_CHOICE = 'NOT_SURE_CHOICE';
export const BLOG_CHOICE = 'BLOG_CHOICE';
export const EBOOKS_CHOICE = 'EBOOKS_CHOICE';
export const EBOOKS_STEP = 'EBOOKS_STEP';
export const BUY_SITUATION_STEP = 'BUY_SITUATION_STEP';
export const NOT_FOUND_YET_CHOICE = 'NOT_FOUND_YET_CHOICE';
export const HIGHER_CHOICE = 'HIGHER_CHOICE';
export const LOWER_CHOICE = 'LOWER_CHOICE';

/** Form Components **/
import CustomerFormComponent1 from './components/forms/customer-form-1';
import CustomerFormComponent2 from './components/forms/customer-form-2';
import CustomerFormComponent3 from './components/forms/customer-form-3';
import CustomerFormComponent4 from './components/forms/customer-form-4';
import MortgageFormComponent1 from './components/forms/mortgage-form-1';
import MortgageFormComponent2 from './components/forms/mortgage-form-2';
import MortgageFormComponent3 from './components/forms/mortgage-form-3';
import MortgageFormComponent5 from './components/forms/mortgage-form-5';
import PropertyFormComponent from './components/forms/property-form';
// import IncomesFormComponent from './components/forms/incomes-form';

/** Trust Banners */
import TrustBanks from './components/trust/trust-banks';

import { MERGE_DATA } from '@/vuex/modules/flower';
import { userAccountURL, webSiteURL, blogURL } from '@/utils/url-builder';
import {
  NEW_MORTGAGE_SLUG,
  BUY_MORTGAGE_SLUG,
  HOME_EQUITY_LOAN_SLUG,
  HOUSE_REMODELING_PRODUCT_MOTIVE,
  DEBT_CONSOLIDATION_PRODUCT_MOTIVE,
  WORK_CAPITAL_PRODUCT_MOTIVE,
} from '@/utils/constants';

const redirectToAccount = store => {
  const data = store.state.data;

  const url = data?.loginURL || userAccountURL(
    '/login',
    {
      queryParams: {
        email: data?.customer?.email,
        dni: data?.customer?.dni,
      },
    },
  );

  window.location.href = url;
};

const nextStepFromProductSelection = product => {
  switch (product) {
    case NEW_MORTGAGE_SLUG:
      return NEW_MORTGAGE_CONDITIONS_STEP;
    case BUY_MORTGAGE_SLUG:
      return PROPERTY_FORM_STEP;
    case HOME_EQUITY_LOAN_SLUG:
      return PROPERTY_FORM_STEP;
    default:
      return false;
  }
};

const steps = {
  [WELCOME_STEP]: {
    question: {
      title: '¡Hola! somos un asesor experto en créditos hipotecarios',
      description: '<span class="has-text-primary">Te asesoramos gratuitamente</span> en alguna de las siguientes soluciones',
    },
    choice({ store }) {
      return {
        component: 'product-choice',
        default: { value: productSelectionParser.stringify(store.state.data.product, store.state.data.motive) },
        items: [
          {
            title: 'Necesito <b>capital de trabajo</b> para mi negocio',
            value: productSelectionParser.stringify(HOME_EQUITY_LOAN_SLUG, WORK_CAPITAL_PRODUCT_MOTIVE),
          },
          {
            title: 'Quiero <b>consolidar mis deudas</b>',
            value: productSelectionParser.stringify(HOME_EQUITY_LOAN_SLUG, DEBT_CONSOLIDATION_PRODUCT_MOTIVE),
          },
          {
            title: 'Necesito <b>remodelar/construir</b> mi casa',
            value: productSelectionParser.stringify(HOME_EQUITY_LOAN_SLUG, HOUSE_REMODELING_PRODUCT_MOTIVE),
          },
          {
            title: 'Quiero <b>trasladar</b> mi crédito hipotecario',
            value: productSelectionParser.stringify(BUY_MORTGAGE_SLUG),
          },
          {
            title: 'Quiero <b>comprar</b> un inmueble',
            value: productSelectionParser.stringify(NEW_MORTGAGE_SLUG),
          },
        ],
      };
    },
    mounted({ store }) {
      const product = store.state.data?.product;

      store.dispatch('trust/setBanner', TrustBanks, { root: true });

      if(!store.state.data?.product) return;

      const stepName = nextStepFromProductSelection(product)

      if(stepName)
        store.dispatch('goToStep', {
          step: getStepByName(nextStepFromProductSelection(product)),
        });
    },
    beforeNext({ store }) {
      const { product, motive } = productSelectionParser.parse(store.getters.lastAnswer.value);

      store.commit(MERGE_DATA, { product, motive });
    },
    nextStep: ({ store: { getters: { lastAnswer: { value }}} }) => {
      const { product } = productSelectionParser.parse(value);

      return nextStepFromProductSelection(product);
    },
  },

  [NEW_MORTGAGE_CONDITIONS_STEP]: {
    progress: 5,
    question: ({ store: { rootGetters } }) => ({
      title: `¡Excelente! Mi nombre es ${rootGetters['adviser/adviser'].name} y te voy a guiar en este proceso 👍`,
      description: 'Primero es necesario que puedas sustentar ingresos para la evaluación del crédito.',
    }),
    choice: {
      items: [
        { title: 'Si, continuemos', value: CONFIRM_CHOICE },
        { title: 'No, no tengo cómo sustentar mis ingresos', value: DECLINE_CHOICE },
      ],
    },
    nextStep({ store: { getters: { lastAnswer: { value }}} }) {
      switch (value) {
        case CONFIRM_CHOICE:
          return PROPERTY_FORM_STEP;
        case DECLINE_CHOICE:
          return NO_INCOMES_SUPPORT_STEP;
      }
    }
  },

  get [NO_INCOMES_SUPPORT_STEP]() {
    return {
      ...this[MORE_INFO_STEP],
      question: {
        title: 'Si no tienes cómo sustentar ingresos, no vamos a poder ayudarte por el momento 😢',
        description: '¿Cómo te gustaríá continuar?',
      },
    };
  },

  [MORE_INFO_STEP]: {
    question: {
      title: '¿Te gustaría que te ayudemos en algo más?',
    },
    choice: {
      items: [
        { title: 'No, eso es todo', value: DECLINE_CHOICE, href: webSiteURL() },
        { title: 'Leer nuestro blog', value: BLOG_CHOICE, href: blogURL() },
        { title: 'Descargar ebooks', value: EBOOKS_CHOICE, href: webSiteURL('/ebooks-financieros-gratuitos')},
      ],
    },
  },

  [LINK_SEND_STEP]: {
    question: {
      title: 'Tu enlace ha sido enviado',
      description: 'Te acabamos de enviar un enlace a tu correo, con el vas a poder ingresar',
    },
    mounted({ store: { state : { data: { customer }}} }) {
      API.token({ email: customer.email });
    },
  },

  [ERROR_STEP]: {
    question: {
      title: 'Ha ocurrido un error',
      description: 'Por favor comunícate con nosotros para poder ayudarte al <a href="tel:+52-55-8526-5672">55-8526-5672</a> o al correo <a href="mailto:ayuda@rebajatuscuentas.com">ayuda@rebajatuscuentas.com</a>.'
    },
  },

  [CUSTOMER_FORM_STEP_1]: {
    progress: 82,
    question: {
      title: '¿A qué correo te enviamos las propuestas de los bancos?',
      description: 'A este correo te enviaremos las propuestas y los resultados de tu pre-calificación',
    },
    form: {
      component: CustomerFormComponent1,
      dataKey: 'customer',
    },
    nextStep: CUSTOMER_FORM_STEP_2,
  },

  [CUSTOMER_FORM_STEP_2]: {
    progress: 84,
    question: {
      title: '¿Cuál es tu número de celular?',
      description: '¡No te haremos llamadas molestas! Es solo para que tu asesor te guíe en el proceso.',
    },
    form: {
      component: CustomerFormComponent2,
      dataKey: 'customer',
    },
    nextStep: CUSTOMER_FORM_STEP_3,
  },

  [CUSTOMER_FORM_STEP_3]: {
    progress: 86,
    question: {
      title: '¿Cuál es tu RFC?',
      description: 'Utilizaremos este dato para poder precalificarte',
    },
    form: {
      component: CustomerFormComponent3,
      dataKey: 'customer',
    },
    nextStep: CUSTOMER_FORM_STEP_4,
  },

  [CUSTOMER_FORM_STEP_4]: {
    progress: 88,
    question: {
      title: 'Por último ¿Cuál es tu nombre?',
      description: '¿A quién tenemos el gusto de asesorar gratuitamente?',
    },
    form: {
      component: CustomerFormComponent4,
      dataKey: 'customer',
    },
    async beforeNext({ store }) {
      const { commit } = store;
      const formData = store.getters.lastAnswer;
      const propertyFormData = store.state.data.property;
      const customerFormData = store.state.data.customer;
      const mortgageFormData = store.state.data.mortgage;
      const incomeCategory = store.state.answers[INCOMES_CATEGORY_STEP];
      const product = store.state.data.product;
      const motive = store.state.data.motive;
      const adviser = store.rootGetters['adviser/adviser'];

      store.dispatch('removeNotification');

      try {
        const searchParams = new URLSearchParams(window.location.search);
        const currencyId = 4;
        const requestData = {
          adviserId: adviser.id,
          product,
          motive,
          firstName: formData.firstName,
          email: customerFormData.email,
          vat: customerFormData.vat,
          mobile: customerFormData.phone,
          source: searchParams.get('utm_source'),
          medium: searchParams.get('utm_medium'),
          campaign: searchParams.get('utm_campaign'),
          referrerId: searchParams.get('referrer_id'),
          // --- Property
          property: {
            value: propertyFormData.value,
            currencyId,
          },
          // --- Mortgage
          ...mortgageFormData ? {
            credit: {
              simpleFee: mortgageFormData.simpleFee,
              credit: mortgageFormData.credit,
              months: mortgageFormData.years * 12,
              insuranceTypeId: mortgageFormData.insuranceTypeId,
              bankId: mortgageFormData.bank.id,
              amortizationType: mortgageFormData.amortizationType,
              currencyId,
            },
          } : {},
          // --- Incomes
          incomes: [
            {
              amount: 15000,
              currencyId,
              incomeTypeId: incomeCategory.value,
            },
          ],
          createDeal: true,
          showReport: false,
        };


        return await retry(
          {
            times: 5,
            interval: 2000,
            errorFilter(err) {
              return err instanceof ServiceUnavailableError;
            },
          },
          (cb) => {
            API.form(humps.decamelizeKeys(requestData))
              .then(({ data: responseData, status }) => {
                if(status === 200) {
                  commit(MERGE_DATA, {
                    token: responseData.token,
                    loginURL: responseData.loginUrl,
                    firstName: responseData.firstName,
                    lastName: responseData.lastName,
                    propertyValue: parseInt(responseData?.propertyValue) || 0,
                  });

                  API.headers({ Authorization: `JWT ${responseData.token}` });
                } else if (status === 202){
                  store.dispatch('addNotification', { type: 'danger', body: `Al parecer ya tienes una cuenta con nosotros. <a href="${userAccountURL('/login')}">Haz click aquí</a> para ingresar a tu cuenta y continuar con tu solicitud.`});

                  return false;
                } else if(status === 209) {
                  store.dispatch('addNotification', { type: 'danger', body: `El DNI que nos proporcionaste no tiene una hipoteca inscrita según la información que nos proporciona las centrales de riesgos. Comunícate con nosotros para continuar al <a href="tel:+52-55-8526-5672">55-8526-5672</a> o al correo <a href="mailto:ayuda@rebajatuscuentas.com">ayuda@rebajatuscuentas.com</a>.`});

                  return false;
                } else if(status === 503) {
                  return new ServiceUnavailableError();
                } else {
                  return new Error('Ha ocurrido un error desconocido');
                }
              })
            .catch((error) => cb(error));
          }
        );
      } catch(error) {
        return ERROR_STEP;
      }
    },
    nextButtonText: 'Crear cuenta y ver propuestas',
    nextStep: SUCCESS_STEP,
  },

  [PROPERTY_FORM_STEP]: {
    progress: 50,
    question({ store }) {
      const product = store.state.data.product;

      return {
        title: do{
          if(product === NEW_MORTGAGE_SLUG) '¿Cuánto vale la propiedad que quieres comprar?';
          else if(product === BUY_MORTGAGE_SLUG) '¿Cuánto vale la propiedad asociado al crédito hipotecario?';
          else if(product === HOME_EQUITY_LOAN_SLUG) '¿Cuánto vale la propiedad que quieres dejar en garantía?';
          else '¿Cual es el valor de la propiedad?';
        },
      };
    },
    form: {
      component: PropertyFormComponent,
      dataKey: 'property',
    },
    nextStep({ store }) {
      const product = store.state.data.product;

      if([NEW_MORTGAGE_SLUG, HOME_EQUITY_LOAN_SLUG].includes(product)) {
        return INCOMES_CATEGORY_STEP;
      } else if(product === BUY_MORTGAGE_SLUG) {
        return MORTGAGE_FORM_STEP_1;
      }
    },
  },

  [MORTGAGE_FORM_STEP_1]: {
    progress: 60,
    question: {
      title: '¿En qué banco tienes tu hipoteca actual?',
      description: 'En base a estos datos te vamos a mostrar las propuestas de crédito.',
    },
    form: {
      component: MortgageFormComponent1,
      dataKey: 'mortgage',
    },
    nextStep: MORTGAGE_FORM_STEP_2,
  },

  [MORTGAGE_FORM_STEP_2]: {
    progress: 62,
    question: {
      title: '¿Cuánto pagas mes a mes? ¿Cuánto es tu mensualidad?',
      description: 'Este monto es el que optimizaremos.',
    },
    form: {
      component: MortgageFormComponent2,
      dataKey: 'mortgage',
    },
    nextStep: MORTGAGE_FORM_STEP_3,
  },

  [MORTGAGE_FORM_STEP_3]: {
    progress: 64,
    question: {
      title: '¿Cuál es el monto de la deuda a la fecha?',
      description: 'Cuánto es tu deuda hoy día, luego de los pagos que has estado realizando.',
    },
    form: {
      component: MortgageFormComponent3,
      dataKey: 'mortgage',
    },
    nextStep: MORTGAGE_FORM_STEP_4,
  },

  [MORTGAGE_FORM_STEP_4]: {
    progress: 66,
    question: {
      title: '¿Qué tipo de mensualidad tienes?',
      description: 'Pagas una mensualidad fija todos los meses o esta se incrementa a lo largo de los años',
    },
    choice: {
      items: [
        { title: 'Mensualidad Fija', value: 'fixed' },
        { title: 'Mensualidad Creciente', value: 'increasing' },
        { title: 'No lo sé', value: null },
      ],
    },
    beforeNext({ store }) {
      const amortizationType = store.getters.lastAnswer.value;

      store.commit(MERGE_DATA, { mortgage: { ...store.state.data.mortgage, amortizationType } });
    },
    nextStep: MORTGAGE_FORM_STEP_5
  },

  [MORTGAGE_FORM_STEP_5]: {
    progress: 68,
    question: {
      title: '¿Cuál años te faltan por pagar?',
      description: 'Cuántos años te faltan pagar para finalizar el crédito',
    },
    form: {
      component: MortgageFormComponent5,
      dataKey: 'mortgage',
    },
    nextStep: INCOMES_CATEGORY_STEP,
  },

  [INCOMES_CATEGORY_STEP]: {
    progress: 70,
    question: {
      title: '¿Cómo percibes tus ingresos?',
      description: 'Coloca la categoría principal de cómo percibes tus ingresos.',
    },
    choice: {
      items: [
        { title: 'Asalariado', text: 'Empleado', value: 1, icon: cloudfrontURL('/icons/income-categories/employee.svg')  },
        { title: 'Independiente', text: 'Trabajador Independiente, Recibo por Honorarios.', value: 2, icon: cloudfrontURL('/icons/income-categories/services.svg')  },
      ],
      scrollOnSelect: true,
    },
    nextStep: CUSTOMER_FORM_STEP_1,
  },

  [SUCCESS_STEP]: {
    progress: 100,
    question: {
      title: '¡Felicidades!',
      description: 'Ahora vamos a pre-calificarte y mostrarte propuestas de crédito para tu solicitud. <br><br>' +
        'Serás redireccionado en 3 segundos',
    },

    mounted({ store }) {
      setTimeout(() => {
        redirectToAccount(store);
      }, 4000);
    },
  },
};

export const getStepByName = name => steps[name];

export default steps;

